import { consumeMcpAuthorizationReturn } from '../lib/mcp-auth-return.js';
import { consumeGitHubConnection, saveGitHubConnection } from '../lib/github-connection.js';
// ─── Auth Callback Page ─────────────────────────────────────────
// Handles GitHub OAuth redirect landing.
//
// KEY DESIGN: This component does NOT independently call Supabase.
// AuthProvider (wrapping the entire app) already processes the hash
// fragment, fires onAuthStateChange, and sets user/session/subscriber.
//
// AuthCallback watches the AuthContext state and OAuth error params:
//   OAuth error params in URL → show OAuthErrorBanner immediately
//   loading=true, no error params → show spinner (up to 5s, then banner)
//   loading=false && user exists → extract provider_token, redirect
//   loading=false && no user, no error params → show NoSessionBanner
//
// provider_token extraction: Supabase only surfaces the GitHub OAuth
// token on the initial callback. We grab it from the session object
// and POST it to the API for storage on the subscriber record.

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { AlertCircle } from 'lucide-react';

/** Parsed OAuth error parameters from Supabase/GoTrue redirect */
export interface OAuthCallbackError {
  error: string;
  error_code: string | null;
  error_description: string | null;
}

/**
 * Parse OAuth error params from URL hash or query string.
 * Supabase/GoTrue may return errors in either location depending on flow.
 * Hash fragment is checked first (GoTrue default), then query string.
 *
 * Exported for unit testing.
 */
export function parseOAuthErrorParams(hash: string, search: string): OAuthCallbackError | null {
  const sources = [
    hash.startsWith('#') ? hash.slice(1) : hash,
    search.startsWith('?') ? search.slice(1) : search,
  ];

  for (const raw of sources) {
    if (!raw) continue;
    const p = new URLSearchParams(raw);
    const error = p.get('error');
    if (error) {
      return {
        error,
        error_code: p.get('error_code'),
        error_description: p.get('error_description'),
      };
    }
  }
  return null;
}

/**
 * Produce a human-readable sentence from an OAuth error_description.
 * Strips raw URLs, long tokens, and stack-trace suffixes; truncates to 150 chars.
 *
 * Exported for unit testing.
 */
export function humanizeOAuthError(description: string | null): string {
  if (!description) return 'An unexpected error occurred. Please try again.';
  const cleaned = description
    .replace(/https?:\/\/\S+/g, '')          // strip raw URLs
    .replace(/\b[A-Za-z0-9+/=]{40,}\b/g, '') // strip long token blobs
    .replace(/\s+/g, ' ')
    .trim();
  // Cap at 150 chars without cutting mid-word
  const truncated =
    cleaned.length > 150 ? cleaned.slice(0, 150).replace(/\s\S*$/, '\u2026') : cleaned;
  return truncated || 'An unexpected error occurred. Please try again.';
}

const backToLoginUrl = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('ocnexus.dev')) {
    return 'https://portal.ocnexus.dev';
  }
  return typeof window !== 'undefined' ? window.location.origin : '/';
};

/** Shared button style for the error banners. */
const tryAgainBtnStyle: React.CSSProperties = {
  padding: '7px 18px',
  fontSize: 'var(--text-sm)',
  background: 'var(--accent, #00E5FF)',
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  cursor: 'pointer',
  fontWeight: 500,
};

/**
 * Error banner displayed when the OAuth provider returned an error.
 * Props-only (no hooks) — testable with renderToStaticMarkup.
 */
export function OAuthErrorBanner({
  message,
  onTryAgain,
}: {
  message: string;
  onTryAgain: () => void;
}) {
  return (
    <>
      <AlertCircle size={28} style={{ color: 'var(--red, #ef4444)' }} />
      <span
        style={{ fontSize: 'var(--text-base, 1rem)', fontWeight: 600, color: 'var(--text)' }}
      >
        Sign-in with GitHub failed
      </span>
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-dim)',
          textAlign: 'center',
          maxWidth: 360,
        }}
      >
        {message}
      </span>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onTryAgain} style={tryAgainBtnStyle}>
          Try again
        </button>
      </div>
      <span
        style={{
          fontSize: 'var(--text-xs, 0.75rem)',
          color: 'var(--text-dim)',
          marginTop: 2,
        }}
      >
        You can also sign in with email instead.
      </span>
    </>
  );
}

/**
 * Banner shown when no session arrives and no explicit OAuth error was returned.
 * Props-only (no hooks) — testable with renderToStaticMarkup.
 */
export function NoSessionBanner({ onTryAgain }: { onTryAgain: () => void }) {
  return (
    <>
      <AlertCircle size={28} style={{ color: 'var(--red, #ef4444)' }} />
      <span
        style={{ fontSize: 'var(--text-base, 1rem)', fontWeight: 600, color: 'var(--text)' }}
      >
        Sign-in didn&apos;t complete
      </span>
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-dim)',
          textAlign: 'center',
          maxWidth: 360,
        }}
      >
        The sign-in session didn&apos;t arrive. This can happen due to a popup
        blocker, a network hiccup, or a browser extension. Please try again.
      </span>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onTryAgain} style={tryAgainBtnStyle}>
          Try again
        </button>
      </div>
      <span
        style={{
          fontSize: 'var(--text-xs, 0.75rem)',
          color: 'var(--text-dim)',
          marginTop: 2,
        }}
      >
        Email sign-in is also available if GitHub keeps failing.
      </span>
    </>
  );
}

/** Time (ms) to wait for a session on the callback route before showing an error banner */
const NO_SESSION_TIMEOUT_MS = 5_000;

export function AuthCallback({ onAuthenticated }: { onAuthenticated: () => void }) {
  const { user, session, loading } = useAuth();

  type ErrorKind = 'oauth' | 'no-session' | null;
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);
  const [oauthMessage, setOauthMessage] = useState<string | null>(null);
  const redirected = useRef(false);
  const tokenStored = useRef(false);
  const oauthErrorOnArrival = useRef(parseOAuthErrorParams(window.location.hash, window.location.search));

  const handleTryAgain = () => {
    window.location.href = backToLoginUrl();
  };

  // ── Step 1: Parse OAuth error params on mount ──────────────────
  // Supabase/GoTrue sends error details in the URL on provider failure.
  // Parse immediately; clean the URL so a page-refresh doesn't
  // re-surface the same banner.
  useEffect(() => {
    const parsed = oauthErrorOnArrival.current;
    if (parsed) {
      // Clean error params from URL so refresh doesn't re-toast
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch {
        // Best-effort: ignore if replaceState is unavailable
      }
      setOauthMessage(humanizeOAuthError(parsed.error_description));
      setErrorKind('oauth');
    }
  }, []); // run once on mount

  // ── Step 2: 5-second timeout for the "no session, no error" case ──
  // If no session materialises within 5s (and no explicit OAuth error
  // was found), show the NoSessionBanner rather than leaving the user
  // staring at a spinner indefinitely.
  useEffect(() => {
    if (errorKind !== null) return; // an error is already shown
    const timer = setTimeout(() => {
      if (!redirected.current && errorKind === null) {
        setErrorKind('no-session');
      }
    }, NO_SESSION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [errorKind]);

  // ── Step 3: React to auth state changes ───────────────────────
  useEffect(() => {
    if (loading) return;
    if (redirected.current) return;
    if (errorKind !== null || oauthErrorOnArrival.current) return;

    if (user && session) {
      redirected.current = true;

      // Capture provider_token only for an explicit GitHub connection return.
      // CRITICAL: provider_token is only present on the initial OAuth callback session.
      // Supabase strips it on subsequent getSession() calls.
      void (async () => {
        const githubReturn = consumeGitHubConnection(user.id);
        const mcpReturn = consumeMcpAuthorizationReturn();
        const onboarding = new URLSearchParams(window.location.search).get('onboarding') === '1';
        let githubStored = false;
        // Login alone must preserve the subscriber's selected credential route.
        // Stale, expired or cross-account connection intent cannot authorize capture.
        if (githubReturn?.sameUser && session.user.id === user.id && !tokenStored.current) {
          tokenStored.current = true;
          try {
            const providerToken = session.provider_token;
            if (providerToken && session.access_token) {
              await saveGitHubConnection(providerToken, session, user.id);
              githubStored = true;
            } else {
              const result = await Promise.race([
                supabase.auth.getSession(),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 3_000)),
              ]);
              const recovered = result?.data.session;
              if (recovered?.provider_token && recovered.access_token && recovered.user.id === user.id) {
                await saveGitHubConnection(recovered.provider_token, recovered, user.id);
                githubStored = true;
              }
            }
          } catch {
            // Submitted credentials and provider/storage exceptions stay out of browser logs.
          }
        }

        // ocn-S418-P0: a fresh signup's magic-link verify redirects here with
        // ?onboarding=1 (see auth-subscriber.ts buildAuthCallbackUrl). Preserve
        // that flag into the portal redirect so the user lands directly in
        // the subscriber chat surface instead of the default overview page —
        // never gated on stripe_customer_id (payment has not happened yet).
        if (mcpReturn) { window.location.assign(mcpReturn); return; }
        if (githubReturn && !onboarding) {
          window.location.assign(githubReturn.path + (githubStored ? '' : '&githubConnection=failed'));
          return;
        }

        // Redirect to portal root — App.tsx handles routing from there
        if (window.location.hostname.includes('ocnexus.dev')) {
          window.location.href = onboarding
            ? 'https://portal.ocnexus.dev/?page=subscriber-chat&onboarding=1'
            : 'https://portal.ocnexus.dev/';
        } else {
          onAuthenticated();
        }
      })();
    } else if (errorKind === null) {
      // loading=false, no user, no explicit OAuth error → auth failed silently
      setErrorKind('no-session');
    }
  }, [loading, user, session, onAuthenticated, errorKind]);

  // ─── Render ──────────────────────────────────────────────────

  const outerStyle: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100vh', gap: 12,
    background: 'var(--bg)', color: 'var(--text-dim)',
  };

  if (errorKind === 'oauth' && oauthMessage) {
    return (
      <div style={outerStyle}>
        <OAuthErrorBanner message={oauthMessage} onTryAgain={handleTryAgain} />
      </div>
    );
  }

  if (errorKind === 'no-session') {
    return (
      <div style={outerStyle}>
        <NoSessionBanner onTryAgain={handleTryAgain} />
      </div>
    );
  }

  return (
    <div style={outerStyle}>
      <div className="spinner" style={{ width: 24, height: 24 }} />
      <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>
        Signing you in&hellip;
      </span>
    </div>
  );
}
